import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { ResultCard, ResultCardHeader } from '../ResultCard';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { isPeriodLabelFormat, useFormatPeriodLabel } from '../use-format-period-label';
import { useFormatReportPeriod } from '../use-format-report-period';
import type { AggregateSummaryPartType } from './aggregate-summary.types';
import { isChartTextKey } from '@insio/shared-types';
import { useChartLabel } from '../chart/use-chart-label';

interface Props {
  part: AggregateSummaryPartType;
}

export function AggregateSummaryPart({ part }: Props) {
  const t = useFeatureTranslation('aiChat');
  const formatAmount = useFormatCurrency();
  const formatPeriod = useFormatReportPeriod();
  const formatPeriodLabel = useFormatPeriodLabel();
  const chartLabel = useChartLabel();
  const isCount = part.metric === 'count';
  const hasRows = part.rows.length > 0;

  const groupLabel = part.groupBy === 'none' ? null : t(`aggregateSummary.groupBy.${part.groupBy}`);

  const formatValue = (value: string, currency: string): string | null =>
    isCount ? value : formatAmount(value, currency);

  // Period buckets ("2012-01") are formatted; keys and label-set values are translated;
  // supplier names are already display text.
  const formatRowLabel = (label: string): string =>
    isPeriodLabelFormat(part.groupBy) && !isChartTextKey(label)
      ? formatPeriodLabel(label, part.groupBy)
      : chartLabel(label, part.labelSet);

  return (
    <ResultCard>
      <ResultCardHeader title={t(`aggregateSummary.metric.${part.metric}`)}>
        <span className='text-foreground-muted text-sm'>
          {formatPeriod(part.dateFrom, part.dateTo)}
        </span>
      </ResultCardHeader>

      {/* Grand value — one row per currency, never combined across currencies */}
      <div className='flex flex-col gap-2xs'>
        {part.totals.length === 0 && (
          <span className='text-foreground-muted text-sm'>{t('aggregateSummary.noData')}</span>
        )}
        {part.totals.map(total => (
          <div key={total.currency} className='flex items-baseline justify-between gap-sm'>
            <span className='text-foreground-muted text-sm'>{t('aggregateSummary.overall')}</span>
            <span className='font-semibold text-foreground-base text-lg'>
              {formatValue(total.value, total.currency)}
            </span>
          </div>
        ))}
      </div>

      <div className='flex items-baseline justify-between gap-sm'>
        <span className='text-foreground-muted text-sm'>{t('aggregateSummary.invoiceCount')}</span>
        <span className='text-foreground-base text-sm'>{part.invoiceCount}</span>
      </div>

      {/* Breakdown table */}
      {hasRows && groupLabel && (
        <div className='flex flex-col gap-2xs mt-xs'>
          <div className='flex items-center justify-between gap-sm border-b border-border-base pb-2xs'>
            <span className='text-foreground-muted text-xs font-medium uppercase tracking-wide'>
              {groupLabel}
            </span>
            <span className='text-foreground-muted text-xs font-medium uppercase tracking-wide'>
              {t(`aggregateSummary.metric.${part.metric}`)}
            </span>
          </div>
          {part.rows.map(row => (
            <div
              key={`${row.label}::${row.currency}`}
              className='flex items-baseline justify-between gap-sm'
            >
              <span className='text-foreground-base text-sm truncate'>
                {formatRowLabel(row.label)}
              </span>
              <span className='text-foreground-base text-sm shrink-0'>
                {formatValue(row.value, row.currency)}
                {!isCount && (
                  <span className='text-foreground-muted text-xs ml-xs'>({row.count})</span>
                )}
              </span>
            </div>
          ))}
          {part.truncated && (
            <span className='text-foreground-muted text-xs'>{t('aggregateSummary.truncated')}</span>
          )}
        </div>
      )}
    </ResultCard>
  );
}
